import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import rehypeHighlight from 'rehype-highlight';
import styled from 'styled-components';
import 'highlight.js/styles/atom-one-light.css';
import { palette } from 'styles/color';

interface MarkdownRendererProps {
  children: string;
}

interface CodeProps extends React.HTMLAttributes<HTMLElement> {
  inline?: boolean;
  children?: React.ReactNode;
}

const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ children }) => {
  const content = children || '';

  const generateId = (text: React.ReactNode): string => {
    if (typeof text === 'string') {
      return text;
    }
    return '';
  };

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[rehypeRaw, rehypeHighlight]}
      components={{
        h1: ({ node, children, ...props }) => <Heading1 {...props}>{children}</Heading1>,
        h2: ({ node, children, ...props }) => <Heading2 {...props}>{children}</Heading2>,
        h3: ({ node, children, ...props }) => (
          <Heading3 id={generateId(children)} {...props}>
            {children}
          </Heading3>
        ),
        h4: ({ node, children, ...props }) => (
          <Heading4 id={generateId(children)} {...props}>
            {children}
          </Heading4>
        ),
        a: ({ node, href, children, ...props }) => {
          const isExternal = typeof href === 'string' && /^https?:\/\//i.test(href);

          return (
            <MarkdownLink
              href={href}
              target={isExternal ? '_blank' : undefined}
              rel={isExternal ? 'noreferrer' : undefined}
              {...props}>
              {children}
            </MarkdownLink>
          );
        },
        p: ({ node, children, className, ...props }) => (
          <Paragraph {...props}>{children}</Paragraph>
        ),

        code: ({ inline, children, ...props }: CodeProps) => {
          const isInline = inline !== undefined ? inline : !String(children).includes('\n');
          // const languageMatch = className?.match(/language-([\w-]+)/);
          // const lang = languageMatch
          //   ? languageMatch[1] === 'curl'
          //     ? 'bash'
          //     : languageMatch[1]
          //   : 'plaintext';

          return isInline ? (
            <InlineCode {...props}>{children}</InlineCode>
          ) : (
            <Pre {...props}>{children}</Pre>
          );
        },
        ul: ({ node, children, ...props }) => <List {...props}>{children}</List>,
        li: ({ node, children, ...props }) => <ListItem {...props}>{children}</ListItem>,
        hr: ({ node, ...props }) => <Divider {...props} />,
        table: ({ children, ...props }) => <Table {...props}>{children}</Table>,
        thead: ({ children, ...props }) => <TableHead {...props}>{children}</TableHead>,
        tbody: ({ children, ...props }) => <TableBody {...props}>{children}</TableBody>,
        tr: ({ children, ...props }) => <TableRow {...props}>{children}</TableRow>,
        th: ({ children, ...props }) => <TableHeader {...props}>{children}</TableHeader>,
        td: ({ children, ...props }) => <TableCell {...props}>{children}</TableCell>
      }}>
      {content}
    </ReactMarkdown>
  );
};

export default MarkdownRenderer;

export const Heading1 = styled.h1`
  font-weight: 800;
`;

export const Heading2 = styled.h2`
  font-weight: 700;
  font-size: 28px;
  height: 100%;
  margin-bottom: 12px;
`;

export const Heading3 = styled.h3`
  font-weight: 600;
  font-size: 22px;
  height: 100%;
  color: ${palette.gray[800]};
  margin: 40px 0 16px 0;
`;

export const Heading4 = styled.h4`
  font-weight: 500;
  font-size: 18px;
  height: 100%;
  color: ${palette.gray[800]};
  margin-bottom: 12px;
`;

export const Paragraph = styled.p`
  font-weight: 400;
  font-size: 14px;
  height: 100%;
  color: ${palette.gray[500]};
  margin-bottom: 16px;
`;

export const InlineCode = styled.span`
  padding: 0 4px;
  height: 20px;
  display: inline-block;
  vertical-align: middle;
  border-radius: 4px;
  background: #dddddd;
  color: ${palette.gray[800]};
  font-weight: 400;
  font-size: 13px;
  line-height: 150%;
  font-family: Menlo, Monaco, 'Courier New', monospace;
  white-space: nowrap;
`;

export const Pre = styled.pre`
  padding: 16px;
  margin-bottom: 16px;
  border-radius: 8px;
  font-family: Menlo, Monaco, 'Courier New', monospace;
  font-size: 12px;
  overflow: auto;
`;

export const List = styled.ul`
  padding-left: 24px;
  list-style-type: disc;
  // color: #0070f3;
`;

export const ListItem = styled.li`
  font-size: 14px;
  margin-bottom: 6px;
  color: ${palette.gray[500]};
`;

export const MarkdownLink = styled.a`
  display: inline;
  color: ${palette.blue[600]};
  font-weight: 500;
  text-decoration: underline;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;

  &:visited {
    color: ${palette.blue[700]};
  }

  &:hover {
    color: ${palette.blue[700]};
  }

  &:focus-visible {
    outline: 2px solid ${palette.blue[300]};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

export const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${palette.gray[300]};
  margin: 40px 0;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 16px;
`;

const TableHead = styled.thead``;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  &:nth-child(even) {
    background-color: ${palette.gray[100]};
  }
`;

const TableHeader = styled.th`
  padding: 9.5px 15px;
  box-sizing: border-box;
  border: 1px solid ${palette.gray[200]};
  background-color: ${palette.gray[100]};

  text-align: center;
  vertical-align: middle;
  font-weight: 600;
  font-size: 14px;
  line-height: 150%;
  color: ${palette.gray[800]};

  white-space: pre;
`;

const TableCell = styled.td`
  padding: 13px 16px;
  box-sizing: border-box;
  border: 1px solid ${palette.gray[300]};
  font-size: 12px;
`;
